import React, { useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, MarkerF, CircleF } from '@react-google-maps/api';
import useQueryGetMe from '@/api/auth/getMe.ts';
import useQueryGetUsers, { AlertStatus, DeviceStatus, IUser } from '@/api/user/getUsers.ts';
import clustering from 'density-clustering';
import useQueryGetSafeZone from '@/api/settings/settings.ts'; // 반경 가져오는 API 추가
import { useTranslation } from 'react-i18next';

interface IProps {
    className?: string;
    safeZoneLocation?: { lat: number; lng: number } | null; // 안심구역 위치 추가
    showSafeZone?: boolean; // 안심구역 표시 여부 추가
    showMyLocation?: boolean; // 내위치 표시 여부 추가
}

export enum MarketTYPE {
    CLUSTER,
    SINGLE
}

export interface IMarker {
    id: number;
    type?: MarketTYPE;
    location?: IMarketLocation;
    alertStatus?: AlertStatus;
    deviceStatus?: DeviceStatus;
    users: IUser[];
}

export interface IMarketLocation {
    latitude?: number;
    longitude?: number;
    accuracy?: number;
    timestamp?: string;
}

export interface IMarkerInfo extends IMarker {
    locationName?: string;
    render?: any;
    address: string;
}

const CACHE__ZOOM_LEVEL = 'dashboard_map__zoom';
const CACHE__CENTER_X = 'dashboard_map__center__longitude';
const CACHE__CENTER_Y = 'dashboard_map__center__latitude';

const DashboardMapDefault = ({ className, safeZoneLocation, showSafeZone = false, showMyLocation = false }: IProps, ref: any) => {
    const [map, setMap] = useState<any>(null);
    const [zoomLevel, setZoomLevel] = useState(0);
    const { data: users } = useQueryGetUsers();
    const { data: me } = useQueryGetMe();
    const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
    const { data: safeZoneData } = useQueryGetSafeZone(); // 서버에서 안심구역 반경 가져오기
    const { t } = useTranslation();
    const navigate = useNavigate();

    // 초기 지도 중심점 계산
    const initialCenter = useMemo(() => {
        if (!users || users.length === 0) {
            return {
                lat: me?.latitude || 37.5759,
                lng: me?.longitude || 126.9768
            };
        }

        // 첫 번째 유효한 위치를 가진 사용자 찾기
        const userWithLocation = users.find(user => 
            user?.location?.latitude && 
            user?.location?.longitude && 
            user?.location?.latitude !== 0 && 
            user?.location?.longitude !== 0
        );

        if (userWithLocation?.location?.latitude && userWithLocation?.location?.longitude) {
            return {
                lat: userWithLocation.location.latitude,
                lng: userWithLocation.location.longitude
            };
        }

        return {
            lat: me?.latitude || 37.5759,
            lng: me?.longitude || 126.9768
        };
    }, [users, me]);

    // LocalStorage에서 저장된 반경 값 가져오고, 없으면 기본값(100) 사용
    const [safeZoneRadius, setSafeZoneRadius] = useState<number>(
        Number(localStorage.getItem("safeZoneRadius")) || 100
    );

    useEffect(() => {
        if (safeZoneData?.safeZoneRadiusM) {
            const newRadius = safeZoneData.safeZoneRadiusM

            if (newRadius !== safeZoneRadius) {
                setSafeZoneRadius(newRadius);
                localStorage.setItem("safeZoneRadius", newRadius.toString()); // 저장
            }
        }
    }, [safeZoneData, safeZoneRadius]);

    useImperativeHandle(ref, () => ({
        setLocation(latitude: number, longitude: number, zoom?: number) {
            map?.panTo({ lat: latitude, lng: longitude });
            if (zoom) {
                map?.setZoom(zoom);
            }
        },
        setUserLocation(latitude: number, longitude: number) {
            setUserLocation({ lat: latitude, lng: longitude });
        },
        clearUserLocation() {
            setUserLocation(null);
        }
    }));

    const openUserInfo = (info?: IMarker) => {
        if (!info || !info.users || info.users.length === 0) return;

        // 첫 번째 사용자의 페이지로 이동
        // window.location.href = \`/user?id=\${info.users[0]?.id}\`;
        navigate(`/user?id=${info.users[0]?.id}`);
    };


    useEffect(() => {
        const handleStorageChange = () => {
            const newRadius = Number(localStorage.getItem("safeZoneRadius")) || 100;
            setSafeZoneRadius(newRadius);
        };

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    const markers: IMarker[] = useMemo(() => {
        const filtered = users?.filter(item => item?.device?.status) || [];
        const radius = new Map<number, number>();
        radius.set(21, 0.000005);
        radius.set(20, 0.00001);
        radius.set(19, 0.00002);
        radius.set(18, 0.00005);
        radius.set(17, 0.0001);
        radius.set(16, 0.0002);
        radius.set(15, 0.0005);
        radius.set(14, 0.001);
        radius.set(13, 0.002);
        radius.set(12, 0.005);
        radius.set(11, 0.01);
        radius.set(10, 0.02);
        radius.set(9, 0.05);
        radius.set(8, 0.1);
        radius.set(7, 0.2);
        radius.set(6, 0.5);
        radius.set(5, 1);
        radius.set(4, 2);
        radius.set(3, 5);
        radius.set(2, 10);
        radius.set(1, 20);

        const dbscan = new clustering.DBSCAN();
        const clusters = dbscan.run(
            filtered?.map(item => [item?.location?.longitude || 0, item?.location?.latitude || 0]) || [],
            radius.get(zoomLevel) ?? 0.000005,
            2
        );

        const markers: IMarker[] = [];

        clusters?.map(ids => {
            const item = filtered[ids[0]];
            const filteredUsers = ids?.map(id => filtered[id]);

            let deviceStatus;
            if (filteredUsers?.find(item => item?.device?.status === DeviceStatus.EMERGENCY)) {
                deviceStatus = DeviceStatus.EMERGENCY;
            } else if (filteredUsers?.find(item => item?.device?.status === DeviceStatus.ONLINE)) {
                deviceStatus = DeviceStatus.ONLINE;
            } else if (filteredUsers?.find(item => item?.device?.status === DeviceStatus.CHARGING)) {
                deviceStatus = DeviceStatus.CHARGING;
            } else {
                deviceStatus = DeviceStatus.OFFLINE;
            }

            markers?.push({
                id: item?.id,
                deviceStatus: deviceStatus,
                location: {
                    latitude: item?.location?.latitude,
                    longitude: item?.location?.longitude,
                    accuracy: item?.location?.accuracy,
                    timestamp: item?.location?.timestamp
                },
                type: MarketTYPE.CLUSTER,
                users: ids?.map(id => filtered[id])
            });
        });

        dbscan?.noise?.map(id => {
            const item = filtered[id];
            markers?.push({
                id: item?.id,
                deviceStatus: item?.device?.status,
                location: {
                    latitude: item?.location?.latitude,
                    longitude: item?.location?.longitude,
                    accuracy: item?.location?.accuracy,
                    timestamp: item?.location?.timestamp
                },
                type: MarketTYPE.SINGLE,
                users: [item]
            });
        });

        return markers;
    }, [users, zoomLevel]);

    return (
        <div className={`${className}`}>
            <div style={{ width: '100%', height: '100%' }}>
                {me && (
                    <GoogleMap
                        onLoad={(mapInstance) => setMap(mapInstance)}
                        center={initialCenter}
                        zoom={18}
                        onZoomChanged={() => {
                            setZoomLevel(map?.getZoom() || 18);
                            localStorage.setItem(CACHE__ZOOM_LEVEL, map?.getZoom()?.toString() || '18');
                        }}
                        onCenterChanged={() => {
                            const center = map?.getCenter();
                            if (center) {
                                localStorage.setItem(CACHE__CENTER_X, center.lng().toString());
                                localStorage.setItem(CACHE__CENTER_Y, center.lat().toString());
                            }
                        }}
                        mapContainerStyle={{ width: '100vw', height: '100vh' }}
                        options={{
                            disableDefaultUI: true, // 모든 기본 UI를 비활성화
                            zoomControl: false, // 확대/축소 버튼 비활성화
                            fullscreenControl: false, // 전체 화면 버튼 비활성화
                            mapTypeControl: false, // 지도/위성 전환 버튼 비활성화
                            streetViewControl: false, // 스트리트뷰 버튼 비활성화
                            gestureHandling: "greedy", // 한 손가락으로 지도 이동 가능하게 설정
                        }}
                    >
                        {markers?.map(item => (
                            <MarkerF
                                key={`marker_${item?.id}`}
                                position={{ lat: item?.location?.latitude || 0, lng: item?.location?.longitude || 0 }}
                                icon={{
                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                        (() => {
                                            let color: string = '#2966F4';
                                            switch (item?.deviceStatus) {
                                                case DeviceStatus.EMERGENCY:
                                                    color = '#FF003D';
                                                    break;
                                                case DeviceStatus.ONLINE:
                                                    color = '#2966F4';
                                                    break;
                                                case DeviceStatus.CHARGING:
                                                    color = '#ADFF45';
                                                    break;
                                                default:
                                                    color = '#BABABA';
                                                    break;
                                            }

                                            // 배경색에 따른 글자색 결정
                                            const textColor = (item?.deviceStatus === DeviceStatus.CHARGING || item?.deviceStatus === DeviceStatus.OFFLINE) ? 'black' : 'white';

                                            return `
                                                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
                                                    <circle cx="40" cy="40" r="38" fill="${color}" stroke="white" stroke-width="3"/>
                                                    <text x="40" y="45" fill="${textColor}" font-size="14" font-family="Arial" font-weight="bold" text-anchor="middle">
                                                        ${item?.users?.[0]?.name || ''}
                                                    </text>
                                                </svg>
                                            `;
                                        })() as string
                                    )}`
                                }}
                                onClick={() => openUserInfo(item)}
                            />
                        ))}

                        {/* 내 위치 마커 */}
                        {showMyLocation && userLocation && (
                            <MarkerF
                                position={userLocation}
                                icon={{
                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                                        <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
                                            <circle cx="40" cy="40" r="38" fill="#0891b2" stroke="white" stroke-width="3"/>
                                            <text 
                                                x="50%" 
                                                y="50%" 
                                                fill="white" 
                                                font-size="14" 
                                                font-family="Arial" 
                                                font-weight="bold" 
                                                text-anchor="middle" 
                                                dominant-baseline="middle">
                                                ${t('dashboard.button.myLocation')}
                                            </text>
                                        </svg>
                                    `)}`
                                }}
                            />
                        )}

                        {/* 안심구역 원 추가 */}
                        {safeZoneLocation && showSafeZone && (
                            <CircleF
                                center={safeZoneLocation}
                                radius={safeZoneRadius}
                                options={{
                                    fillColor: '#0000FF55',
                                    strokeColor: '#0000FF',
                                    strokeWeight: 2,
                                    fillOpacity: 0.3,
                                }}
                            />
                        )}
                    </GoogleMap>
                )}
            </div>
        </div>
    );
};

export default React.forwardRef(DashboardMapDefault);
